import { useState, useRef, useEffect } from "react";
import type { FunctionComponent } from "react";
import { Dialog } from "~/components/ui";
import { type TimelineTableItem, getTimelineItemTitle } from "@noa/ui";
import { useCurrentLanguage } from "@noa/i18n";
import cn from "classnames";
import { TimelineCard, CardTitle } from "./chat-message-timeline-card";
import { AuthenticatedImage } from "./chat-message-authenticated-image";
import { HamburgerMenu } from "~/components/buttons/hamburger-menu";

interface ChatMessageTimelineProps {
  items: TimelineTableItem[];
  title?: string;
}

// SIDE_PAD centers the selected card vertically: (frame 340 - bar 36 - card 156) / 2
const SIDE_PAD = 74;
const ITEM_H = 164; // card height h-39 (156px) + mb-2 gap (8px)
const RAIL_ROW_H = 24;
const RAIL_TOP_PAD = 22;

function scrollCardsTo(idx: number, cardScrollRef: React.RefObject<HTMLDivElement | null>) {
  cardScrollRef.current?.scrollTo({ top: idx * ITEM_H, behavior: "instant" });
}

export const ChatMessageTimeline: FunctionComponent<ChatMessageTimelineProps> = ({ items, title }) => {
  const appLanguage = useCurrentLanguage();
  const [selected, setSelected] = useState(0);
  const [expandedCardItem, setExpandedCardItem] = useState<TimelineTableItem | null>(null);
  // The item outlives `open` until the close animation ends, so the dialog does not blank while fading out.
  const [isExpandedOpen, setIsExpandedOpen] = useState(false);
  const [isRailOpen, setIsRailOpen] = useState(() => window.innerWidth >= 640);
  const railRef = useRef<HTMLDivElement>(null);
  const cardScrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = railRef.current;

    if (!el) return;

    const targetY = RAIL_TOP_PAD + selected * RAIL_ROW_H + RAIL_ROW_H / 2;
    el.scrollTo({ top: targetY - el.clientHeight / 2, behavior: "instant" });
  }, [selected]);

  useEffect(() => {
    const el = cardScrollRef.current;

    if (!el) return;

    let debounce: ReturnType<typeof setTimeout> | undefined;
    const onScroll = () => {
      clearTimeout(debounce);
      debounce = setTimeout(() => {
        setSelected(Math.max(0, Math.min(items.length - 1, Math.round(el.scrollTop / ITEM_H))));
      }, 100);
    };
    el.addEventListener("scroll", onScroll);
    return () => {
      clearTimeout(debounce);
      el.removeEventListener("scroll", onScroll);
    };
  }, [items.length]);

  return (
    <>
      <div className="my-4 flex flex-col h-85 overflow-hidden text-noa border border-noa bg-noa-inset rounded-md">
        <div className="h-9 shrink-0 flex items-center px-2 md:pl-4 border-b border-noa">
          <HamburgerMenu isActive={isRailOpen} onClick={() => setIsRailOpen((v) => !v)} />
          {title && (
            <>
              <span className="flex-1 text-center text-sm font-semibold truncate px-2">{title}</span>
              <div className="w-5 shrink-0" />
            </>
          )}
        </div>

        <div className="flex flex-1 overflow-hidden relative">
          <div
            ref={railRef}
            className={cn(
              "chat-timeline-rail overflow-y-auto overflow-x-hidden bg-noa-inset",
              "transition-transform duration-300 sm:transition-[width] sm:translate-x-0",
              "absolute inset-y-0 left-0 z-10 w-45 sm:relative sm:inset-auto sm:z-auto",
              isRailOpen ? "translate-x-0 sm:w-45 sm:shrink-0" : "-translate-x-full sm:w-0"
            )}
          >
            <div
              className="relative"
              style={{
                height: items.length * RAIL_ROW_H + RAIL_TOP_PAD * 2,
                paddingTop: RAIL_TOP_PAD,
                paddingBottom: RAIL_TOP_PAD,
              }}
            >
              <div className="absolute left-4.5 top-8.5 bottom-8.5 w-px bg-noa-border" />
              {items.map((item, i) => {
                const isSel = i === selected;
                return (
                  <button
                    key={i}
                    onClick={() => scrollCardsTo(i, cardScrollRef)}
                    className={cn(
                      "absolute w-45 left-0 right-0 flex items-center gap-2 px-2.5 border-0 cursor-pointer text-left font-[inherit] transition-[background,color,opacity] duration-150",
                      isSel
                        ? "opacity-100 bg-noa-hover text-noa-primary-active"
                        : "opacity-70 bg-transparent text-noa-muted"
                    )}
                    style={{ top: RAIL_TOP_PAD + i * RAIL_ROW_H, height: RAIL_ROW_H }}
                  >
                    <span className="w-3.5 flex justify-center shrink-0">
                      <span
                        className={cn(
                          "w-2.75 h-2.75 rounded-full transition-colors duration-150 ml-0.75",
                          isSel ? "bg-noa-primary-active" : "bg-noa-text-muted"
                        )}
                      />
                    </span>
                    <span
                      className={cn(
                        "text-xs md:text-sm font-mono whitespace-nowrap tracking-[0.2px]",
                        isSel ? "font-bold" : "font-normal"
                      )}
                    >
                      {getTimelineItemTitle(item, appLanguage)}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          <div
            ref={cardScrollRef}
            className="chat-timeline-cards flex-1 overflow-y-scroll snap-y snap-mandatory"
            style={{ scrollPaddingTop: SIDE_PAD }}
          >
            <div className="shrink-0" style={{ height: SIDE_PAD }} />
            {items.map((item, i) => (
              <div key={i} className="snap-start mb-2 px-4 box-border">
                <div className="h-39">
                  <TimelineCard
                    item={item}
                    distance={Math.abs(i - selected)}
                    locale={appLanguage}
                    onExpand={() => {
                      setExpandedCardItem(item);
                      setIsExpandedOpen(true);
                    }}
                  />
                </div>
              </div>
            ))}
            <div className="shrink-0" style={{ height: SIDE_PAD }} />
          </div>
        </div>
      </div>

      <Dialog
        open={isExpandedOpen}
        onCancel={() => setIsExpandedOpen(false)}
        afterClose={() => setExpandedCardItem(null)}
        footer={null}
        width={800}
        title={expandedCardItem ? <CardTitle item={expandedCardItem} locale={appLanguage} /> : null}
        classNames={{ body: "overflow-y-auto max-h-[75vh]" }}
      >
        {expandedCardItem?.cardDetailedText?.map((detail, i) => (
          <div key={i} className="mb-4">
            {detail.text && <p className="m-0 text-sm">{detail.text}</p>}
            {detail.imageUrls.length > 0 && (
              <div className="flex flex-wrap gap-3 mt-2">
                {detail.imageUrls.map((url, ii) => (
                  <AuthenticatedImage key={ii} url={url} alt={detail.text} />
                ))}
              </div>
            )}
          </div>
        ))}
      </Dialog>
    </>
  );
};
